export function parseStudentDocsPayload(res) {
  if (!res || typeof res !== "object") {
    return { templates: [], uploaded: [] };
  }

  const layers = [res, res.data, res.data && res.data.data].filter(
    (x) => x && typeof x === "object"
  );

  for (let i = 0; i < layers.length; i++) {
    const layer = layers[i];
    const templates = layer.templates || layer.template || layer.required;
    const uploaded = layer.uploaded || layer.uploadedDocs || layer.documents;

    if (Array.isArray(templates) || Array.isArray(uploaded)) {
      return {
        templates: Array.isArray(templates) ? templates : [],
        uploaded: Array.isArray(uploaded) ? uploaded : [],
      };
    }
  }

  return { templates: [], uploaded: [] };
}

export function parseDocTypesPayload(res) {
  if (Array.isArray(res)) return res;
  if (res && Array.isArray(res.data)) return res.data;
  if (res && res.data && Array.isArray(res.data.data)) return res.data.data;
  return [];
}

function normalizeDocTitle(title) {
  return String(title || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

export function normalizeUploadedDoc(doc) {
  if (!doc || typeof doc !== "object") return doc;
  const docTypeID =
    doc.docTypeID || doc.typeID || doc.documentTypeID || doc.doc_type_id;
  return docTypeID ? { ...doc, docTypeID: docTypeID } : { ...doc };
}

export function docMatches(a, b) {
  if (!a || !b) return false;

  const aNorm = normalizeUploadedDoc(a);
  const bNorm = normalizeUploadedDoc(b);

  if (aNorm.id && bNorm.id && aNorm.id === bNorm.id) return true;
  if (aNorm.docTypeID && bNorm.docTypeID && aNorm.docTypeID === bNorm.docTypeID)
    return true;
  if (aNorm.docTypeID && bNorm.id && aNorm.docTypeID === bNorm.id) return true;
  if (bNorm.docTypeID && aNorm.id && bNorm.docTypeID === aNorm.id) return true;

  const ta = normalizeDocTitle(aNorm.meta_title || aNorm.title);
  const tb = normalizeDocTitle(bNorm.meta_title || bNorm.title);
  return ta && tb && ta === tb;
}

export function docTypeAppliesToClass(docType, className) {
  const cn = String(className || "").trim();
  if (!cn) return true;

  const raw = docType.classnames;
  if (!raw || !String(raw).trim()) return true;

  const names = String(raw)
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);

  if (names.includes("all classes")) return true;

  const lower = cn.toLowerCase();
  return names.some(
    (n) => n === lower || lower.includes(n) || n.includes(lower)
  );
}

export function buildTemplatesFromDocTypes(docTypes, className) {
  return (docTypes || [])
    .filter((dt) => docTypeAppliesToClass(dt, className))
    .map((dt) => ({
      id: 0,
      docTypeID: dt.id,
      meta_title: dt.title,
      url: "",
      status: "",
    }));
}

export function buildTemplatesFromAllDocTypes(docTypes) {
  return (docTypes || []).map((dt) => ({
    id: 0,
    docTypeID: dt.id,
    meta_title: dt.title,
    url: "",
    status: "",
  }));
}

export function mergeStudentDocs(templates, uploaded) {
  const mergedTemplates = (templates || []).map((t) => ({ ...t }));
  const extras = [];

  (uploaded || []).forEach((doc) => {
    const normalized = normalizeUploadedDoc(doc);
    const idx = mergedTemplates.findIndex((t) => docMatches(t, normalized));
    if (idx >= 0) {
      mergedTemplates[idx] = { ...mergedTemplates[idx], ...normalized };
    } else {
      extras.push(normalized);
    }
  });

  if (!mergedTemplates.length && extras.length) {
    return {
      templates: extras.map((doc) => ({ ...doc })),
      uploaded: [],
    };
  }

  return {
    templates: mergedTemplates,
    uploaded: extras,
  };
}

export function resolveTypeWiseDocuments(
  templates,
  uploaded,
  docTypes,
  className
) {
  const typeTemplates = buildTemplatesFromDocTypes(docTypes, className);
  const allTypeTemplates = buildTemplatesFromAllDocTypes(docTypes);
  const initialTemplates =
    templates && templates.length ? templates : typeTemplates;
  const { templates: mergedTemplates, uploaded: extras } = mergeStudentDocs(
    initialTemplates,
    uploaded
  );

  const list = mergedTemplates.map((doc) => ({ ...doc }));

  allTypeTemplates.forEach((dt) => {
    if (!list.some((item) => docMatches(item, dt))) {
      list.push({ ...dt });
    }
  });

  extras.forEach((doc) => {
    if (!list.some((item) => docMatches(item, doc))) {
      list.push({ ...doc });
    }
  });

  return list.filter(
    (doc) => doc && (doc.meta_title || doc.title || doc.url || doc.docTypeID)
  );
}

export function splitResolvedDocList(list, docTypes, className) {
  const requiredTypeIds = new Set(
    buildTemplatesFromDocTypes(docTypes, className).map((dt) => dt.docTypeID)
  );
  const templates = [];
  const uploaded = [];

  (list || []).forEach((doc) => {
    const typeId =
      doc.docTypeID || doc.typeID || doc.documentTypeID || doc.doc_type_id;
    const isRequiredType = typeId && requiredTypeIds.has(typeId);

    if (isRequiredType) {
      templates.push({ ...doc });
      return;
    }

    const hasContent =
      doc.id ||
      doc.url ||
      (doc.status && String(doc.status).trim());

    if (hasContent) {
      uploaded.push({ ...doc });
    } else if (typeId) {
      templates.push({ ...doc });
    }
  });

  return { templates, uploaded };
}
